// The diff reader's minimap (components/Minimap.svelte): the open file in
// miniature down the reader's right edge, the shape of its code drawn on a
// canvas (`shape`) with marks over it for where its notes are.
//
// Both come from the rendered diff, measured: each thing's top and height
// in the reader's scrolled content. So the strip follows whatever the
// reader shows (folds, reviewed stubs, open threads, a composer) at its
// rendered height, never a line count that drifts from it. This module
// only scales, lays out and merges; the component measures and paints.

/** What a mark stands for. Each kind has its lane and color (Minimap.svelte). */
export type MarkKind =
  | 'unreviewed'
  | 'thread'
  | 'resolved'
  | 'draft'
  | 'span'
  | 'annotation'
  | 'composer'
  | 'cursor';

/** A thing in the reader's content: its kind, its top and height there (px), and what hovering its mark says. */
export interface Measured {
  kind: MarkKind;
  top: number;
  height: number;
  title?: string;
}

/** A mark on the strip: its top and height there (px), and what it says. */
export interface Mark {
  kind: MarkKind;
  top: number;
  height: number;
  title: string;
}

/** The least height a mark of each kind is drawn at, so a one-line change in a long file still shows. */
export const MIN_PX: Readonly<Record<MarkKind, number>> = {
  unreviewed: 2,
  thread: 3,
  resolved: 2,
  draft: 3,
  span: 3,
  annotation: 3,
  composer: 3,
  cursor: 2,
};

/** Marks of a kind closer than this on the strip (px) are drawn as one. */
export const MERGE_PX = 1;

/**
 * The marks for `items` on a strip `strip` px high, standing for content
 * `total` px high: each scaled, at least MIN_PX of its kind, and kept
 * inside the strip; then marks of one kind that touch (within MERGE_PX)
 * merged into one, which says how many it stands for. Ordered by kind,
 * then top. Nothing for an empty reader or strip.
 */
export function minimapMarks(items: readonly Measured[], total: number, strip: number): Mark[] {
  if (!(total > 0) || !(strip > 0)) return [];
  const scale = strip / total;
  const byKind = new Map<MarkKind, { top: number; height: number; title: string; n: number }[]>();
  const sorted = [...items].filter((i) => Number.isFinite(i.top) && Number.isFinite(i.height)).sort((a, b) => a.top - b.top);
  for (const i of sorted) {
    const min = MIN_PX[i.kind];
    const height = Math.min(strip, Math.max(min, i.height * scale));
    const top = Math.max(0, Math.min(strip - height, i.top * scale));
    const list = byKind.get(i.kind) ?? [];
    const last = list.at(-1);
    if (last && top <= last.top + last.height + MERGE_PX) {
      last.height = Math.max(last.height, top + height - last.top);
      last.n++;
    } else list.push({ top, height, title: i.title ?? '', n: 1 });
    byKind.set(i.kind, list);
  }
  const out: Mark[] = [];
  for (const [kind, list] of byKind) for (const m of list) out.push({ kind, top: m.top, height: m.height, title: m.n > 1 && m.title ? `${m.title} (and ${m.n - 1} more here)` : m.title });
  return out;
}

/** The box on the strip for what the reader shows: its scroll position and height, over its content's. */
export function viewportBox(scrollTop: number, clientHeight: number, scrollHeight: number, strip: number): { top: number; height: number } {
  if (!(scrollHeight > 0) || !(strip > 0)) return { top: 0, height: 0 };
  const height = Math.max(8, Math.min(strip, (clientHeight / scrollHeight) * strip));
  const top = Math.max(0, Math.min(strip - height, (scrollTop / scrollHeight) * strip));
  return { top, height };
}

/**
 * Where the reader scrolls to for a press at `y` px down the strip: what
 * is there centered in it, kept within what it can scroll to.
 */
export function scrollFor(y: number, strip: number, clientHeight: number, scrollHeight: number): number {
  if (!(strip > 0)) return 0;
  const at = (Math.max(0, Math.min(strip, y)) / strip) * scrollHeight;
  return Math.max(0, Math.min(scrollHeight - clientHeight, at - clientHeight / 2));
}

// The code's shape.

/** The strip's width (px), and diff.css's room for it beside the reader. */
export const STRIP_PX = 60;
/** Where code is drawn across the strip (px): after the unreviewed edge, before the note lane. */
export const CODE_X: readonly [number, number] = [5, 52];
/** The gap between the base and head halves of a split row (px). */
export const SPLIT_GAP = 2;
/**
 * Columns of code a half (or a unified row) shows; text past them is cut.
 * A half is ~22px, so 48 keeps a column near half a pixel (a device pixel
 * on a dense screen): indentation and line shape still read, and most
 * code's shape is in its first 48 columns.
 */
export const COLS = 48;
/** The tallest a line's bar is drawn (px), however tall its row scales to: a code line, not a block. */
export const BAR_PX = 2;

/** How a run of a line's text is colored: its syntax class, or plain. */
export type Tone = 'text' | 'punct' | 'keyword' | 'flow' | 'string' | 'number' | 'type' | 'call' | 'comment';
/** What a line is in the diff. */
export type LineKind = 'context' | 'removed' | 'added' | 'moved';

/** The tone of text inside an element with these classes (the highlighter's syn-*, tokens.css). */
export function toneOf(classes: string): Tone {
  const has = (c: string) => classes.includes(c);
  if (has('syn-comment') || has('syn-in-comment')) return 'comment';
  if (has('syn-keyword')) return 'keyword';
  if (has('syn-string')) return 'string';
  if (has('syn-number') || has('syn-constant') || has('syn-escape')) return 'number';
  if (has('syn-type') || has('syn-module') || has('syn-tag')) return 'type';
  if (has('syn-function')) return 'call';
  if (has('syn-macro') || has('syn-attribute') || has('syn-label')) return 'flow';
  if (has('syn-operator') || has('syn-punctuation')) return 'punct';
  return 'text';
}

/** A stretch of a line's text with one tone, as the cell renders it. */
export interface Segment {
  text: string;
  tone: Tone;
}
/** A word of a line: where it starts (column), how long, and its tone. */
export interface Run {
  col: number;
  len: number;
  tone: Tone;
}

/**
 * A line's words, by column (a tab to the next multiple of four), each
 * kept to one tone: what the strip draws for it, the spaces between them
 * left out so its indentation and shape show. Nothing past COLS.
 */
export function runsOf(segments: readonly Segment[]): Run[] {
  const out: Run[] = [];
  let col = 0;
  for (const { text, tone } of segments) {
    for (const ch of text) {
      if (col >= COLS) return out;
      if (ch === '\t') { col = (Math.floor(col / 4) + 1) * 4; continue; }
      if (ch === ' ' || ch === '\n' || ch === '\r') { col++; continue; }
      const last = out.at(-1);
      if (last && last.tone === tone && last.col + last.len === col) last.len++;
      else out.push({ col, len: 1, tone });
      col++;
    }
  }
  return out;
}

/** One side of a rendered row: base or head of a split row (`half` 0 or 1), or the whole of a unified one (`half` null). */
export interface ShapeCell {
  half: 0 | 1 | null;
  kind: LineKind;
  runs: readonly Run[];
}
/** A rendered row of code, measured in the reader's content. */
export interface ShapeRow {
  top: number;
  height: number;
  cells: readonly ShapeCell[];
}
/**
 * What a band across the strip is: the whole of a change, as a tinted
 * region (not reviewed yet, or reviewed); a change's head, as a rule at its
 * top; an unchanged stretch's head; or folded lines.
 */
export type BandKind = 'change' | 'reviewed' | 'head' | 'context' | 'folded';
export interface ShapeBand {
  kind: BandKind;
  top: number;
  height: number;
}

/** How a rectangle is painted: a line kind's tint behind it, a word (its tone, on a line of its kind), a band, or (thin) a changed line's lane mark. */
export type Paint = `tint:${Exclude<LineKind, 'context'>}` | `word:${LineKind}:${Tone}` | `band:${BandKind}` | `lane:${Exclude<LineKind, 'context'>}`;
/** A rectangle on the strip (px). */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
  paint: Paint;
}

const RANK: Readonly<Record<LineKind, number>> = { context: 0, added: 1, removed: 1, moved: 2 };
const weight = (c: ShapeCell) => RANK[c.kind] * 1e6 + c.runs.reduce((n, r) => n + r.len, 0);

/**
 * The code's shape on a strip `strip` px high and STRIP_PX wide, for
 * content `total` px high, at `dpr` device pixels to the px: each row's
 * words as bars (indented as the line is, as long as its words, cut at
 * COLS), in its half of a split row, over its kind's tint when changed;
 * and the bands for changes, heads and folds, under all of it. Rows that
 * fall on one device pixel line are drawn as one, each half by its most
 * telling line there (a change over context, then the fuller one), so a
 * long file costs no more than the strip has lines. Bands first, then
 * tints, then words: the order they paint in.
 */
export function shape(rows: readonly ShapeRow[], bands: readonly ShapeBand[], total: number, strip: number, dpr = 1): Rect[] {
  if (!(total > 0) || !(strip > 0)) return [];
  const scale = strip / total;
  const px = 1 / Math.max(1, dpr);
  const snap = (y: number) => Math.floor(y / px) * px;
  const [x0, x1] = CODE_X;
  const out: Rect[] = [];
  // Changes' regions under the rules and folds drawn on them.
  const region = (b: ShapeBand) => (b.kind === 'change' || b.kind === 'reviewed' ? 0 : 1);
  for (const b of [...bands].sort((p, q) => region(p) - region(q))) {
    if (!Number.isFinite(b.top) || !Number.isFinite(b.height)) continue;
    const y = snap(Math.max(0, Math.min(strip - px, b.top * scale)));
    // A head is a rule, however tall it scales to; the rest take their height.
    const h = Math.max(px, b.kind === 'head' ? Math.min(BAR_PX, b.height * scale) : b.height * scale);
    // A change's region spans the strip, edge and ticks included, so it reads as a block; rules and folds the code's width.
    out.push(region(b) ? { x: x0, y, w: x1 - x0, h, paint: `band:${b.kind}` } : { x: 0, y, w: STRIP_PX, h, paint: `band:${b.kind}` });
  }
  // Each device pixel line's row: its top, its slot's height, and each half's line.
  const lines = new Map<number, { y: number; h: number; halves: Map<string, ShapeCell> }>();
  for (const r of rows) {
    if (!Number.isFinite(r.top) || !Number.isFinite(r.height)) continue;
    const y = snap(Math.max(0, Math.min(strip - px, r.top * scale)));
    const key = Math.round(y / px);
    const line = lines.get(key) ?? { y, h: Math.max(px, r.height * scale), halves: new Map() };
    for (const c of r.cells) {
      const k = String(c.half);
      const was = line.halves.get(k);
      if (!was || weight(c) > weight(was)) line.halves.set(k, c);
    }
    lines.set(key, line);
  }
  const tints: Rect[] = [];
  const words: Rect[] = [];
  for (const { y, h, halves } of lines.values()) {
    const bar = Math.max(px, Math.min(BAR_PX, snap(h * 0.7)));
    for (const c of halves.values()) {
      const half = (x1 - x0 - SPLIT_GAP) / 2;
      const [left, width] = c.half === null ? [x0, x1 - x0] : [x0 + c.half * (half + SPLIT_GAP), half];
      if (c.kind !== 'context') tints.push({ x: left, y, w: width, h, paint: `tint:${c.kind}` });
      const col = width / COLS;
      // The bar sits in the middle of its row's slot, as text does in its line.
      const at = snap(y + (h - bar) / 2);
      for (const r of c.runs) words.push({ x: left + r.col * col, y: at, w: Math.min(r.len, COLS - r.col) * col, h: bar, paint: `word:${c.kind}:${r.tone}` });
    }
  }
  return [...out, ...tints, ...words];
}

// The thin strip: the signal without the code's shape.

/**
 * The thin strip's width (px): the unreviewed edge (1–4), a lane each for
 * removed and added lines side by side (5–11, as a split row has both),
 * and the note ticks (12–15). The old lane strip's width less its gaps:
 * every lane a few pixels, so a one-line change still shows.
 */
export const THIN_PX = 16;
/** Where each changed line's lane is across the thin strip (px): moved lines take both. */
export const THIN_LANES: Readonly<Record<Exclude<LineKind, 'context'>, readonly [number, number]>> = { removed: [5, 3], added: [8, 3], moved: [5, 6] };

/**
 * The thin strip for the same rows and bands as `shape`, THIN_PX wide:
 * changes as regions and their heads as rules across it, folded lines a
 * dotted band; each changed line a mark in its kind's lane, unchanged
 * lines nothing. Lines on one device pixel line are drawn as one in each
 * lane, a moved line taking both lanes over the removed and added there.
 * Bands first, then lanes.
 */
export function thinShape(rows: readonly ShapeRow[], bands: readonly ShapeBand[], total: number, strip: number, dpr = 1): Rect[] {
  if (!(total > 0) || !(strip > 0)) return [];
  const scale = strip / total;
  const px = 1 / Math.max(1, dpr);
  const snap = (y: number) => Math.floor(y / px) * px;
  const out: Rect[] = [];
  const region = (b: ShapeBand) => (b.kind === 'change' || b.kind === 'reviewed' ? 0 : 1);
  for (const b of [...bands].sort((p, q) => region(p) - region(q))) {
    // An unchanged stretch's head says nothing this narrow.
    if (b.kind === 'context' || !Number.isFinite(b.top) || !Number.isFinite(b.height)) continue;
    const y = snap(Math.max(0, Math.min(strip - px, b.top * scale)));
    const h = Math.max(px, b.kind === 'head' ? Math.min(BAR_PX, b.height * scale) : b.height * scale);
    out.push({ x: 0, y, w: THIN_PX, h, paint: `band:${b.kind}` });
  }
  // Each device pixel line's marks: its top, its slot's height, and the kinds on it.
  const lines = new Map<number, { y: number; h: number; kinds: Set<Exclude<LineKind, 'context'>> }>();
  for (const r of rows) {
    if (!Number.isFinite(r.top) || !Number.isFinite(r.height)) continue;
    const changed = r.cells.filter((c) => c.kind !== 'context');
    if (!changed.length) continue;
    const y = snap(Math.max(0, Math.min(strip - px, r.top * scale)));
    const key = Math.round(y / px);
    const line = lines.get(key) ?? { y, h: Math.max(px, r.height * scale), kinds: new Set() };
    for (const c of changed) line.kinds.add(c.kind as Exclude<LineKind, 'context'>);
    lines.set(key, line);
  }
  for (const { y, h, kinds } of lines.values()) {
    const drawn = kinds.has('moved') ? (['moved'] as const) : ([...kinds] as Exclude<LineKind, 'context'>[]);
    for (const k of drawn) out.push({ x: THIN_LANES[k][0], y, w: THIN_LANES[k][1], h, paint: `lane:${k}` });
  }
  return out;
}

const TONE_VAR: Readonly<Record<Exclude<Tone, 'text' | 'punct'>, string>> = {
  keyword: '--syn-keyword',
  flow: '--syn-flow',
  string: '--syn-string',
  number: '--syn-number',
  type: '--syn-type',
  call: '--syn-call',
  comment: '--syn-comment',
};
const KIND_VAR: Readonly<Record<Exclude<LineKind, 'context'>, string>> = { removed: '--bad', added: '--good', moved: '--syn-keyword' };

/**
 * The color (a custom property of tokens.css) and opacity a paint is drawn
 * in: unchanged lines quiet, in their syntax's colors but faint and their
 * plain words muted; changed lines plain, over the diff's own word-mark
 * tints, plain words in their kind's color; a change as a solid region in
 * the warning background until reviewed, then the calmer review green, its
 * head a rule; an unchanged stretch's head a fainter rule, and folded lines
 * a dotted muted band; a thin strip's lane mark in its line kind's color.
 */
export function paintStyle(p: Paint): { color: string; alpha: number } {
  const [layer, a, b] = p.split(':') as [string, string, string | undefined];
  if (layer === 'band') {
    if (a === 'change') return { color: '--warnbg', alpha: 1 };
    if (a === 'reviewed') return { color: '--goodbg', alpha: 0.7 };
    if (a === 'head') return { color: '--line', alpha: 1 };
    if (a === 'folded') return { color: '--muted', alpha: 0.45 };
    return { color: '--line', alpha: 0.7 };
  }
  if (layer === 'lane') return { color: KIND_VAR[a as Exclude<LineKind, 'context'>], alpha: 1 };
  if (layer === 'tint') return a === 'moved' ? { color: '--syn-keyword', alpha: 0.2 } : { color: a === 'removed' ? '--mark-del' : '--mark-add', alpha: 1 };
  const kind = a as LineKind;
  const tone = b as Tone;
  const quiet = kind === 'context';
  if (tone === 'text' || tone === 'punct') {
    const alpha = (quiet ? 0.5 : 1) * (tone === 'punct' ? 0.6 : 1);
    return { color: quiet ? '--muted' : KIND_VAR[kind as Exclude<LineKind, 'context'>], alpha };
  }
  return { color: TONE_VAR[tone], alpha: quiet ? 0.6 : 1 };
}
